import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Divider, Paper, Stack, Step, stepLabelClasses, useTheme } from '@mui/material';

import {
  CreateProcessRequestDto,
  getGetProcessPermissionsQueryKey,
  getGetProcessTalentsQueryKey,
  ProcessResponseDTO,
  useAddTalentsToProcess,
  useCreateProcess,
  useGetProcessById,
  useUpdateProcess,
  useUpdateProcessTalentRegistrationQuestionsForm
} from '@/api/generated';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal';
import { SimpleItem } from '@/components/fields/Location/types';
import Form from '@/components/Form/Form';
import LoadingButton from '@/components/LoadingButton';
import { VerticalStepLabel, VerticalStepper } from '@/components/VerticalStepper';
import { useProcessPermissions } from '@/hooks/permissions/useProcessPermissions';
import { useClearLocationState } from '@/hooks/useClearLocationState';
import { OrganizationStructureProvider, useOrganizationStructure } from '@/hooks/useOrganizationStructure';
import { ReadonlyProvider } from '@/hooks/useReadonly';
import { useSnackbar } from '@/hooks/useSnackbar';
import { PROCESS_SETTINGS_STEP_KEYS, ROUTER_PATHS, toProcess } from '@/root/linker';
import {
  accessInvitationMappingPresave,
  createAccessInvitationDefaultValues,
  createBasicInfoDefaultValues,
  createTalentRegistrationDefaultValues,
  createTestPropertiesDefaultValues,
  DEFAULT_TALENTS,
  testSettingsMappingPresave
} from '@/views/ProcessSettingsForm/defaults';

import CreateProcessHeader from './CreateProcessHeader';
import { getSteps } from './defaults';

interface CreateProcessContent {
  onProcessCreate: (newProcessId: string) => void;
  createdProcessId: string | null;
  process?: ProcessResponseDTO;
}

const CreateProcessContent: FC<CreateProcessContent> = ({ onProcessCreate, createdProcessId, process }) => {
  const { spacing, palette } = useTheme();
  const { t } = useTranslation(['processes', 'filters', 'settings']);
  const navigate = useNavigate();
  const { clearState } = useClearLocationState();
  const { watch, getValues } = useFormContext();
  const { flatStructure } = useOrganizationStructure();

  const locationFormValue = watch(PROCESS_SETTINGS_STEP_KEYS.LOCATION);
  const basicInfoFormValue = watch(PROCESS_SETTINGS_STEP_KEYS.BASIC_INFO);

  const mergedProcessValue = useMemo(
    () => ({
      name: locationFormValue?.name,
      organization: locationFormValue?.organization,
      location: locationFormValue?.location,
      assessmentType: basicInfoFormValue?.assessmentType,
      startDate: basicInfoFormValue?.availability?.start,
      endDate: basicInfoFormValue?.availability?.end
    }),
    [
      locationFormValue?.name,
      locationFormValue?.organization,
      locationFormValue?.location,
      basicInfoFormValue?.assessmentType,
      basicInfoFormValue?.availability?.start,
      basicInfoFormValue?.availability?.end
    ]
  );

  const [showCongratulationsModal, setShowCongratulationsModal] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const [isFullScreenMode, setIsFullScreenMode] = useState(false);
  const { showMessage } = useSnackbar();
  const { mutateAsync: addTalentsToProcess, isLoading: isTalentsAdding } = useAddTalentsToProcess();
  const { mutateAsync: updateProcess, isLoading: isProcessUpdating } = useUpdateProcess();
  const { mutateAsync: updateTalentRegistrationForm, isLoading: isProcessTalentFormUpdating } =
    useUpdateProcessTalentRegistrationQuestionsForm();
  const { mutateAsync: createProcess, isLoading: isProcessCreating } = useCreateProcess();

  const queryClient = useQueryClient();
  const testCount = process?.catalogItems?.length || 0;

  const { value: processPermissions, isLoading: isProcessPermissionsLoading } = useProcessPermissions({
    variables: { processId: createdProcessId! }
  });

  const isLoading =
    isProcessCreating ||
    isProcessUpdating ||
    isProcessTalentFormUpdating ||
    isTalentsAdding ||
    isProcessPermissionsLoading;

  const toggleFullScreenMode = useCallback((isFullScreen: boolean) => {
    setIsFullScreenMode(isFullScreen);
  }, []);

  const handleCreateProcess = useCallback(async () => {
    const {
      [PROCESS_SETTINGS_STEP_KEYS.LOCATION]: locationData,
      [PROCESS_SETTINGS_STEP_KEYS.BASIC_INFO]: basicInfoData
    } = getValues();

    try {
      const currentLocation = flatStructure?.find(({ slug }: SimpleItem) => slug === locationData.location);
      const locationType = currentLocation?.type;

      const processData = {
        name: locationData.name,
        organization: locationData.organization,
        assessmentType: basicInfoData.assessmentType,
        description: basicInfoData.description,
        startDate: basicInfoData.availability?.start,
        endDate: basicInfoData.availability?.end,
        location: {
          slug: locationData.location,
          type: locationType
        }
      } as CreateProcessRequestDto;
      const newProcess = await createProcess({
        data: processData
      });

      if (!newProcess) {
        throw new Error('No process data');
      }

      onProcessCreate(newProcess.id!);
      clearState();
      setShowCongratulationsModal(true);

      queryClient.invalidateQueries(getGetProcessPermissionsQueryKey(newProcess.id!));
    } catch (e) {
      showMessage(t('common:error.somethingWentWrong'));

      // eslint-disable-next-line no-console
      console.error(e);
    }
  }, [getValues, flatStructure, createProcess, onProcessCreate, clearState, queryClient, showMessage, t]);

  const handleGoForward = useCallback(() => {
    setActiveStep(step => step + 1);
  }, []);

  const handleGoBack = useCallback(() => {
    setActiveStep(step => step - 1);
  }, []);

  const handleTalentsSave = useCallback(async () => {
    try {
      const values = getValues();
      const talentIds = values[PROCESS_SETTINGS_STEP_KEYS.TALENTS]?.talentsToAdd || [];

      if (!createdProcessId) throw new Error('No process data');

      if (talentIds.length) {
        await addTalentsToProcess({
          id: createdProcessId,
          data: { talentIds }
        });
        showMessage(t('settings:processSettings.form.alerts.saveSuccess'));
      }

      handleGoForward();
      await queryClient.invalidateQueries(getGetProcessTalentsQueryKey(createdProcessId));
    } catch (error) {
      showMessage(t('settings:processSettings.form.alerts.saveError'));
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }, [getValues, createdProcessId, addTalentsToProcess, showMessage, t, queryClient, handleGoForward]);

  const handleLaterConfirmation = useCallback(() => {
    setShowCongratulationsModal(false);
    navigate(toProcess({ processId: createdProcessId! }));
  }, [createdProcessId, navigate, setShowCongratulationsModal]);

  const handleSuccessConfirmation = useCallback(async () => {
    setShowCongratulationsModal(false);
    handleGoForward();
  }, [handleGoForward]);

  const handleUpdateProcess = useCallback(async () => {
    try {
      const {
        [PROCESS_SETTINGS_STEP_KEYS.BASIC_INFO]: basicInfoSettings,
        [PROCESS_SETTINGS_STEP_KEYS.LOCATION]: locationSettings
      } = getValues();

      if (!createdProcessId) {
        return;
      }

      const basicInfoData = {
        name: locationSettings.name,
        location: locationSettings.organization,
        assessmentType: basicInfoSettings.assessmentType,
        description: basicInfoSettings.description,
        startDate: basicInfoSettings.availability?.start,
        endDate: basicInfoSettings.availability?.end
      };
      await updateProcess({
        id: createdProcessId,
        data: basicInfoData
      });

      showMessage(t('settings:processSettings.form.alerts.saveSuccess'));
      handleGoForward();
    } catch (error) {
      showMessage(t('settings:processSettings.form.alerts.saveError'));
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }, [getValues, createdProcessId, updateProcess, showMessage, t, handleGoForward]);

  const handleSettingsSave = useCallback(async () => {
    const {
      [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_ACCESS_INVITATION]: accessInvitationSettings,
      [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TEST_PROPERTIES]: testPropertiesSettings,
      [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TALENT_REGISTRATION]: talentRegistrationSettings
    } = getValues();

    if (!createdProcessId) {
      return;
    }

    try {
      const accessInvitationData = accessInvitationMappingPresave(accessInvitationSettings);
      const { testSettings } = testSettingsMappingPresave(testPropertiesSettings);
      await Promise.all([
        updateProcess({
          id: createdProcessId,
          data: {
            ...accessInvitationData,
            testSettings
          }
        }),
        updateTalentRegistrationForm({
          id: createdProcessId,
          data: {
            external: talentRegistrationSettings?.external,
            internal: talentRegistrationSettings?.internal,
            translations: talentRegistrationSettings?.translations || {}
          }
        })
      ]);

      showMessage(t('settings:processSettings.form.alerts.saveSuccess'));
      navigate(toProcess({ processId: createdProcessId }));
    } catch (error) {
      // eslint-disable-next-line no-console, i18next/no-literal-string
      console.error('Settings save error:', error);
      showMessage(t('settings:processSettings.form.alerts.saveError'));
    }
  }, [getValues, createdProcessId, updateProcess, updateTalentRegistrationForm, showMessage, t, navigate]);

  const { canUpdateSettings, canManageTests, canAddTalents } = processPermissions || {};

  const steps = useMemo(
    () =>
      getSteps(t, {
        defaultGoForward: handleGoForward,
        defaultGoBack: handleGoBack,
        onCreateProcess: handleCreateProcess,
        onSaveSettings: handleSettingsSave,
        onTalentsSave: handleTalentsSave,
        testCount,
        processId: createdProcessId!,
        processData: process,
        isFullScreenMode,
        toggleFullScreenMode,
        onUpdateProcess: handleUpdateProcess,
        canAddTalents,
        canManageTests,
        canUpdateSettings
      }),
    [
      t,
      handleGoForward,
      handleGoBack,
      handleCreateProcess,
      handleSettingsSave,
      handleTalentsSave,
      testCount,
      createdProcessId,
      process,
      isFullScreenMode,
      toggleFullScreenMode,
      handleUpdateProcess,
      canAddTalents,
      canManageTests,
      canUpdateSettings
    ]
  );

  return (
    <ReadonlyProvider readonly={!!processPermissions && !canUpdateSettings}>
      <CreateProcessHeader process={mergedProcessValue} />
      <Stack direction="row" flexGrow={1} sx={{ padding: spacing(3, 5) }}>
        {!isFullScreenMode && (
          <VerticalStepper
            activeStep={activeStep}
            sx={{ alignSelf: 'flex-start', padding: spacing(3, 2), width: spacing(30) }}
          >
            {steps.map(({ key, title }) => (
              <Step key={key}>
                <VerticalStepLabel
                  sx={{
                    [`& .${stepLabelClasses.label}`]: {
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start'
                    }
                  }}
                >
                  {title}
                </VerticalStepLabel>
              </Step>
            ))}
          </VerticalStepper>
        )}
        <Paper
          elevation={0}
          sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, backgroundColor: 'background.default' }}
        >
          <>
            <Box flexBasis={0} flexGrow={1} overflow="auto" sx={steps[activeStep]?.sx}>
              {steps[activeStep]?.content}
            </Box>
            {!isFullScreenMode && (
              <>
                <Divider sx={{ marginTop: 'auto', borderColor: palette.grey[400] }} />
                <Stack
                  direction="row"
                  sx={{ padding: spacing(2.5, 2, 2), gap: spacing(1.5), justifyContent: 'space-between' }}
                >
                  <Box>
                    {steps[activeStep]?.onGoBack && (
                      <Button onClick={steps[activeStep]?.onGoBack}>
                        {steps[activeStep].backText || t('common:actions.back')}
                      </Button>
                    )}
                  </Box>
                  <LoadingButton
                    type="button"
                    color="primary"
                    variant="contained"
                    isLoading={isLoading}
                    onClick={steps[activeStep]?.onGoForward || handleGoForward}
                  >
                    {steps[activeStep].forwardText || t('common:actions.next')}
                  </LoadingButton>
                </Stack>
              </>
            )}
          </>
        </Paper>
      </Stack>
      <ConfirmationModal
        open={showCongratulationsModal}
        title={t('processes:layouts.processCreate.confirmationModal.title', { name: process?.name })}
        confirmButtonLabel={t('processes:layouts.processCreate.confirmationModal.buttons.now')}
        discardButtonLabel={t('processes:layouts.processCreate.confirmationModal.buttons.later')}
        illustration={<Box component="img" width={132} src="/images/congratulations.svg" />}
        onConfirm={handleSuccessConfirmation}
        onDiscard={handleLaterConfirmation}
        onClose={handleSuccessConfirmation}
      />
    </ReadonlyProvider>
  );
};

const CreateProcess = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [createdProcessId, setCreatedProcessId] = useState<string | null>(null);
  const { data: process } = useGetProcessById(createdProcessId!, {
    query: {
      enabled: !!createdProcessId
    }
  });

  const locationSlugInState = location.state && (location.state as { location?: string }).location;

  useEffect(() => {
    if (!locationSlugInState && !createdProcessId) {
      navigate(ROUTER_PATHS.PROCESSES, { replace: true });
    }
  }, [locationSlugInState, createdProcessId, navigate]);

  const defaultValues = useMemo(
    () => ({
      [PROCESS_SETTINGS_STEP_KEYS.LOCATION]: process
        ? { name: process?.name, organization: process?.organization?.slug, location: process?.location?.slug }
        : location.state,
      [PROCESS_SETTINGS_STEP_KEYS.BASIC_INFO]: createBasicInfoDefaultValues(process || {}),
      [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_ACCESS_INVITATION]: createAccessInvitationDefaultValues(process || {}),
      [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TEST_PROPERTIES]: createTestPropertiesDefaultValues(process || {}),
      [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TALENT_REGISTRATION]: createTalentRegistrationDefaultValues({}),
      [PROCESS_SETTINGS_STEP_KEYS.TALENTS]: { ...DEFAULT_TALENTS }
    }),
    [location.state, process]
  );

  const form = useForm({
    defaultValues
  });
  const { watch } = form;
  const locationFormValue = watch(PROCESS_SETTINGS_STEP_KEYS.LOCATION);

  useEffect(() => {
    form.reset(defaultValues);
  }, [process?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!locationSlugInState && !createdProcessId) {
    return null;
  }

  return (
    <OrganizationStructureProvider organizationSlug={locationFormValue?.organization}>
      <Form
        form={form}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          textAlign: 'start'
        }}
      >
        <CreateProcessContent
          onProcessCreate={setCreatedProcessId}
          createdProcessId={createdProcessId}
          process={process}
        />
      </Form>
    </OrganizationStructureProvider>
  );
};

export default CreateProcess;
